/**
 * Resolves a v1.0 `Dynamic*`-style bound value against the surface's data model. Documents encode a
 * binding as `{ $bind: '/json/pointer/path' }` (see the golden corpus); anything else is a literal,
 * returned unchanged. An unresolved path returns `undefined` — never throws — so the adapter falls
 * back to its component's empty state per the format's edge-case contract.
 */
export function resolveBoundValue(bound: unknown, dataModel: Record<string, unknown>): unknown {
  if (bound && typeof bound === 'object' && !Array.isArray(bound) && typeof (bound as { $bind?: unknown }).$bind === 'string') {
    return resolveJsonPointer(dataModel, (bound as { $bind: string }).$bind);
  }
  return bound;
}

/**
 * Pointer segments that must never be walked. `{ $bind: '/constructor' }` otherwise
 * resolves to a function, which React throws on as a child, and `/__proto__` hands a
 * document author a handle on the prototype chain of the host's data model.
 */
const FORBIDDEN_POINTER_SEGMENTS = new Set(['__proto__', 'constructor', 'prototype']);

function resolveJsonPointer(root: Record<string, unknown>, pointer: string): unknown {
  if (pointer === '' || pointer === '/') return root;
  const segments = pointer.replace(/^\//, '').split('/').map(unescapeToken);

  let current: unknown = root;
  for (const segment of segments) {
    if (current == null) return undefined;
    if (FORBIDDEN_POINTER_SEGMENTS.has(segment)) return undefined;
    if (Array.isArray(current)) {
      const index = Number(segment);
      current = Number.isInteger(index) ? current[index] : undefined;
    } else if (typeof current === 'object') {
      current = (current as Record<string, unknown>)[segment];
    } else {
      return undefined;
    }
  }
  return current;
}

function unescapeToken(token: string): string {
  return token.replaceAll('~1', '/').replaceAll('~0', '~');
}
